import { darken, alpha, lighten } from '@mui/material/styles'
import { initDataTheme, isDarkMode, optInForDS } from 'src/Utils/designSystem'

const HEADER_BG_COLOR = '#1e2869'
const PRIMARY_COLOR = '#1E285A'
const SECONDARY_COLOR = '#3C32C8'
const TERTIARY_COLOR = '#2D2D87'

const BLACK = '#000000'
const ERROR = '#FF3B3B'
const YELLOW = '#FFD200'
const BLUE = '#5CC9FF'
const GREEN = '#64C82D'
const WHITE = '#FFFFFF'
const VIOLET = '#8800ff'

initDataTheme()
const usingDS = optInForDS()
const usingDarkMode = usingDS && isDarkMode()

const lightColors = {
  primary: PRIMARY_COLOR,
  secondary: SECONDARY_COLOR,
  tertiary: TERTIARY_COLOR,
  body: '#F4F4F6',
  text: TERTIARY_COLOR,
  linkText: SECONDARY_COLOR,
  sidePanelBackground: WHITE,
}

const darkColors = {
  primary: PRIMARY_COLOR,
  secondary: SECONDARY_COLOR,
  tertiary: TERTIARY_COLOR,
  body: '#F4F4F6',
  text: TERTIARY_COLOR,
  linkText: SECONDARY_COLOR,
  sidePanelBackground: darken(HEADER_BG_COLOR, 0.3),
}

const themedColors = usingDarkMode ? darkColors : lightColors

const colors = {
  ...themedColors,
  error: ERROR,
  errorText: '#DD0000',
  errorBackground: lighten(ERROR, 0.8),
  warning: YELLOW,
  warningText: '#D2AD00',
  warningBackground: lighten(YELLOW, 0.8),
  noContact: alpha(BLACK, 0.55),
  noContactSemiLight: lighten(alpha(BLACK, 0.55), 0.2),
  noContactLight: '#D1D1D1',
  noContactText: WHITE,
  info: BLUE,
  infoBackground: lighten(BLUE, 0.8),
  success: GREEN,
  successText: '#3A7E15',
  successBackground: lighten(GREEN, 0.8),
  disabled: '#DDDDDD',
  disabledText: alpha(BLACK, 0.55),
  disabledLight: '#D1D1D1',
  irregularData: lighten(TERTIARY_COLOR, 0.2),
  irregularDataText: WHITE,
  orange: '#FA9664',
  sectionHeader: alpha(PRIMARY_COLOR, 0.18),
  sectionHeaderHover: alpha(PRIMARY_COLOR, 0.28),
  sectionSubHeader: alpha(PRIMARY_COLOR, 0.1),
  sectionSubHeaderHover: alpha(PRIMARY_COLOR, 0.2),
  sectionBackground: alpha(PRIMARY_COLOR, 0.1),
  sectionAlternateRow: alpha(PRIMARY_COLOR, 0.1),
  sectionAitSeparator: '#e0e0e0',
  tableCollapseBackground: alpha(PRIMARY_COLOR, 0.05),
  tableHeadHover: alpha(PRIMARY_COLOR, 0.7),
  hr: '#808080',
  sheet: '#FFF',
  weather: {
    sun: YELLOW,
    rain: '#00AAFF',
    plusDegrees: '#ff6666',
    plusDegreesToday: '#DD0000',
    minusDegrees: '#00AAFF',
    minusDegreesToday: '#006395',
  },
  fontSize: '0.875rem',
}

const lightLineColors = [
  colors.weather.rain,
  colors.success,
  colors.warning,
  '#BA1C30',
  '#702C8C',
  '#7F7E80',
  '#DB6917',
  '#463397',
  '#D485B2',
  '#EBCE2B',
  colors.primary,
  '#6F340D',
  '#C0BD7F',
  '#2B3514',
  '#4277B6',
  '#DF8461',
  '#E1A11A',
  '#91218C',
  '#E8E948',
  '#7E1510',
  '#92AE31',
  '#D32B1E',
  '#1D1D1D',
  '#96CDE6',
  '#5FA641',
]

const darkLineColors = [
  '#1E1E1E',
  '#FFFFFF',
  '#3C32C8',
  '#FF6B6B',
  lighten('#FF3B3B', 0.2),
  '#FFD700',
  lighten('#FFD200', 0.2),
  alpha(BLACK, 0.55),
  lighten(alpha(BLACK, 0.55), 0.2),
  '#D1D1D1',
  VIOLET,
  lighten('#5CC9FF', 0.2),
  '#7FFF00',
  lighten('#64C82D', 0.2),
  '#DDDDDD',
  alpha(BLACK, 0.55),
  lighten('#2D2D87', 0.2),
  VIOLET,
  '#FA9664',
  alpha('#1E285A', 0.28),
  alpha('#1E285A', 0.1),
  alpha('#1E285A', 0.2),
  alpha('#1E285A', 0.1),
  alpha('#1E285A', 0.1),
  '#e0e0e0',
  alpha('#1E285A', 0.05),
  alpha('#1E285A', 0.7),
  '#808080',
  '#1E1E1E',
  '#FFD200',
  '#00AAFF',
  '#ff6666',
  '#DD0000',
  '#00AAFF',
  '#006395',
]

const lineColors = usingDarkMode ? darkLineColors : lightLineColors

export default {
  ...colors,
  chartColors: {
    lines: lineColors,
    bands: lineColors,
    referenceCurve: ['#ccc', '#aaa', '#aaa'],
    alarms: {
      error: alpha(colors.error, 0.4),
      warning: alpha(colors.warning, 0.8),
    },
    alarmText: {
      error: darken(colors.error, 0.15),
      warning: darken(colors.warning, 0.15),
    },
    weatherFocus: colors.sectionHeader,
    wind: '#5FA641',
    padding: alpha(colors.disabledLight, 0.6),
    subordinate: '#ccc',
  },
}
